import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttributionLinkButtonIcon,
  getHardcodedLinkSourceLabel,
} from 'common/constants/attribution-link-source';
import { Marketplaces } from 'common/constants/enums';
import { lengthRestrictions } from 'common/constants/length-restrictions';
import { utilCopyToClipboard } from 'common/utils';
import {
  amazonProductAsinValidationSchema,
  walmartProductIdValidationSchema,
} from 'common/validationSchemas';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { FC, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { productsApiSlice } from 'store/api/products-api/products-api';

import amazonLogoUrl from '../../../../../../../../assets/images/logos/amazon-icon.png';
import walmartLogoUrl from '../../../../../../../../assets/images/logos/walmart-icon.png';
import { ProductImage } from '../product-image/product-image';
import type { FieldProduct } from '../products-list';
import { GenerateLinkModalV2 } from './generate-link-modal-v2';
import { styles } from './product-card.styles';

const ENABLE_PRODUCT_LINKING = false;

interface IProductCardProps {
  index: number;
  isSelected?: boolean;
  item: FieldProduct;
  onDelete: (index: number) => void;
  onSelect?: () => void;
}

export const ProductCard: FC<IProductCardProps> = ({
  index,
  isSelected,
  item,
  onDelete,
  onSelect,
}) => {
  const {
    control,
    formState: { errors },
    getValues,
    register,
    setValue,
  } = useFormContext<IPageFormV2>();

  const [isGenerateLinkModalOpen, setIsGenerateLinkModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [localLinkInput, setLocalLinkInput] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState('');

  const [fetchFullProductData] = productsApiSlice.useLazyGetFullProductDataQuery();

  const watchedTitle = useWatch({
    control,
    name: `products.${index}.title`,
  });

  const linkedProductId = useWatch({
    control,
    name: `products.${index}.linked_product_id`,
  });

  const isHardcodedLink = useWatch({
    control,
    name: `products.${index}.isHardcodedLink`,
  });
  const pageUrl = useWatch({
    control,
    name: `products.${index}.pageUrl`,
  });
  const hardcodedLinkSource = useWatch({
    control,
    name: `products.${index}.hardcodedLinkSource`,
  });

  const publisher = useWatch({ control, name: 'publisher' });
  const region = publisher?.country || 'us';

  const formProduct = getValues(`products.${index}`);
  const product = formProduct || item;
  const hasHardcodedLink = !!(isHardcodedLink ?? product.isHardcodedLink);
  const productPageUrl = pageUrl ?? product.pageUrl;
  const productHardcodedLinkSource = hardcodedLinkSource ?? product.hardcodedLinkSource;
  const currentTitle = watchedTitle ?? product.title ?? '';
  const isTitleOverLimit = currentTitle.length > lengthRestrictions.product.title;

  const isBestPick = index === 0;

  const isAmazon = product.product_marketplace
    ? product.product_marketplace === Marketplaces.Amazon
    : productPageUrl?.includes('amazon') || /^[a-zA-Z0-9]{10}$/.test(product.product_id);

  const linkedMarketplaceName = isAmazon ? 'Walmart' : 'Amazon';
  const linkedMarketplacePlaceholder = isAmazon ? 'e.g. 1840281673' : 'e.g. B08ZVWDQQ1';
  const linkedMarketplaceIcon = isAmazon ? walmartLogoUrl : amazonLogoUrl;

  useEffect(() => {
    if (!ENABLE_PRODUCT_LINKING) return;

    if (!linkedProductId && product?.product_id) {
      const allProducts = getValues('products') || [];
      const sibling = allProducts.find(
        (p, i) => i !== index && String(p.linked_product_id) === String(product.product_id),
      );
      if (sibling && sibling.product_id) {
        setValue(`products.${index}.linked_product_id`, String(sibling.product_id));
      }
    }
  }, [linkedProductId, product?.product_id, getValues, index, setValue]);

  const handleLinkProduct = async () => {
    if (!ENABLE_PRODUCT_LINKING) return;

    setLinkError('');
    const sanitizedInput = localLinkInput.trim();
    if (!sanitizedInput) return;

    if (sanitizedInput === String(product.product_id)) {
      setLinkError('Cannot link a product to itself.');
      return;
    }

    const targetMarketplace = isAmazon ? Marketplaces.Walmart : Marketplaces.Amazon;
    const targetSchema = isAmazon
      ? walmartProductIdValidationSchema
      : amazonProductAsinValidationSchema;

    try {
      targetSchema.validateSync({ product_id: sanitizedInput });
    } catch (e: any) {
      setLinkError(e.message || 'Invalid ID format');
      return;
    }

    const allProducts = getValues('products') || [];

    const isAlreadyLinkedByOther = allProducts.some(
      (p, i) => i !== index && String(p.linked_product_id) === sanitizedInput,
    );
    if (isAlreadyLinkedByOther) {
      setLinkError('This ID is already linked to another product on this page.');
      return;
    }

    setLinkLoading(true);
    try {
      const siblingIdx = allProducts.findIndex((p) => String(p.product_id) === sanitizedInput);

      if (siblingIdx !== -1) {
        const sibling = allProducts[siblingIdx];

        if (sibling.product_marketplace !== targetMarketplace) {
          setLinkError(`Cannot link to another ${isAmazon ? 'Amazon' : 'Walmart'} product.`);
          return;
        }

        if (
          sibling.linked_product_id &&
          String(sibling.linked_product_id) !== String(product.product_id)
        ) {
          setLinkError('Target product is already linked to something else.');
          return;
        }

        setValue(`products.${index}.linked_product_id`, sanitizedInput, { shouldDirty: true });
        setValue(`products.${siblingIdx}.linked_product_id`, String(product.product_id), {
          shouldDirty: true,
        });

        setLocalLinkInput('');
        return;
      }

      const res = await fetchFullProductData({
        id: sanitizedInput,
        marketplace: targetMarketplace,
        options: { region },
      });

      if (res.isError || !res.data) {
        setLinkError('Product not found in database. Import it first.');
        return;
      }

      if (
        res.data.linked_product_id &&
        String(res.data.linked_product_id) !== String(product.product_id)
      ) {
        setLinkError('Target product is already linked to another item in the database.');
        return;
      }

      setValue(`products.${index}.linked_product_id`, sanitizedInput, { shouldDirty: true });
      setLocalLinkInput('');
    } catch (e: any) {
      setLinkError(e.message || 'Failed to link product');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlinkProduct = () => {
    if (!ENABLE_PRODUCT_LINKING) return;

    setValue(`products.${index}.linked_product_id`, null, { shouldDirty: true });

    if (linkedProductId) {
      const allProducts = getValues('products') || [];
      const siblingIdx = allProducts.findIndex(
        (p) => String(p.product_id) === String(linkedProductId),
      );
      if (siblingIdx !== -1) {
        if (String(allProducts[siblingIdx].linked_product_id) === String(product.product_id)) {
          setValue(`products.${siblingIdx}.linked_product_id`, null, { shouldDirty: true });
        }
      }
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!ENABLE_PRODUCT_LINKING) {
      onDelete(index);
      return;
    }

    if (linkedProductId) {
      setIsDeleteDialogOpen(true);
    } else {
      onDelete(index);
    }
  };

  const handleConfirmDeleteAndKeep = () => {
    setIsDeleteDialogOpen(false);
    onDelete(index);
  };

  const handleConfirmDeleteAndUnlink = () => {
    setIsDeleteDialogOpen(false);
    handleUnlinkProduct();
    onDelete(index);
  };

  return (
    <Box onClick={onSelect} sx={[styles.card, isSelected && styles.cardSelected] as SxProps<Theme>}>
      {isGenerateLinkModalOpen && (
        <GenerateLinkModalV2
          asin={product.product_id}
          initialSource={productHardcodedLinkSource}
          onClose={() => setIsGenerateLinkModalOpen(false)}
          productIndex={index}
        />
      )}

      {ENABLE_PRODUCT_LINKING && (
        <Dialog
          fullWidth
          maxWidth="sm"
          onClose={() => setIsDeleteDialogOpen(false)}
          open={isDeleteDialogOpen}
        >
          <DialogTitle>Remove Linked Product</DialogTitle>
          <DialogContent>
            <Typography>
              This product is currently linked to{' '}
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  verticalAlign: 'middle',
                }}
              >
                <Box
                  component="img"
                  src={linkedMarketplaceIcon}
                  sx={styles.linkedMarketplaceIcon}
                />
                <strong>{linkedProductId}</strong>
              </Box>
              . Do you want to keep this link in the database, or permanently break the link before
              removing the product from this page?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button color="error" onClick={handleConfirmDeleteAndUnlink} variant="outlined">
              Break Link & Remove
            </Button>
            <Button
              color="error"
              disableElevation
              onClick={handleConfirmDeleteAndKeep}
              variant="contained"
            >
              Keep Link & Remove
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={styles.header}>
        <TextField
          {...register(`products.${index}.value`, {
            validate: (value) => {
              if (!isBestPick && value?.trim().toLowerCase() === 'best pick') {
                return 'Reserved for the 1st product only';
              }
              return true;
            },
          })}
          disabled={isBestPick}
          error={!!errors.products?.[index]?.value}
          helperText={errors.products?.[index]?.value?.message}
          placeholder="e.g. Runner Up"
          size="small"
          sx={styles.stickerInput}
        />
      </Box>

      <Box sx={styles.imageContainer}>
        <ProductImage
          alt={product.title}
          src={product.imgMedium || product.imgLarge}
          sx={styles.image}
        />
      </Box>

      <Box sx={styles.infoRow}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip
            title={`${
              product.product_marketplace === Marketplaces.Walmart ? 'Walmart' : 'Amazon'
            } Product`}
          >
            <Box
              component="img"
              src={isAmazon ? amazonLogoUrl : walmartLogoUrl}
              sx={styles.marketplaceLogo}
            />
          </Tooltip>
          <Tooltip title="Copy ID">
            <Box onClick={() => utilCopyToClipboard(product.product_id)} sx={styles.asinContainer}>
              <Typography sx={styles.asinText}>{product.product_id}</Typography>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        </Box>
        {isAmazon && (
          <Tooltip title={hasHardcodedLink ? productPageUrl : ''}>
            <span>
              <IconButton
                disabled={!hasHardcodedLink}
                onClick={() => productPageUrl && utilCopyToClipboard(productPageUrl)}
                size="small"
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {ENABLE_PRODUCT_LINKING && (
        <Box sx={styles.linkedProductContainer}>
          <Box sx={styles.linkedProductHeader}>
            <Box component="img" src={linkedMarketplaceIcon} sx={styles.linkedMarketplaceIcon} />
            <Typography color="text.secondary" fontWeight="500" variant="caption">
              Link to {linkedMarketplaceName}
            </Typography>
          </Box>

          {linkedProductId ? (
            <Box sx={styles.linkedSuccessBox}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography fontWeight="600" variant="body2">
                    {linkedProductId}
                  </Typography>
                </Box>
              </Box>
              <Tooltip title="Unlink product">
                <IconButton color="error" onClick={handleUnlinkProduct} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box>
              <Box sx={styles.linkedInputWrapper}>
                <TextField
                  disabled={linkLoading}
                  error={!!linkError}
                  fullWidth
                  onChange={(e) => {
                    setLocalLinkInput(e.target.value);
                    setLinkError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLinkProduct();
                    }
                  }}
                  placeholder={linkedMarketplacePlaceholder}
                  size="small"
                  sx={{ '& .MuiInputBase-root': { backgroundColor: 'white' } }}
                  value={localLinkInput}
                />
                <Button
                  disabled={!localLinkInput.trim() || linkLoading}
                  disableElevation
                  onClick={handleLinkProduct}
                  size="small"
                  sx={{ height: '36px', minWidth: '70px', textTransform: 'none' }}
                  variant="contained"
                >
                  {linkLoading ? <CircularProgress color="inherit" size={20} /> : 'Link'}
                </Button>
              </Box>
              {linkError && (
                <Typography
                  color="error"
                  sx={{ mt: 0.5, display: 'block', lineHeight: 1.2 }}
                  variant="caption"
                >
                  {linkError}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Box sx={styles.titleContainer}>
        <TextField
          {...register(`products.${index}.title`)}
          error={!!errors.products?.[index]?.title || isTitleOverLimit}
          fullWidth
          multiline
          placeholder="Product Title"
          rows={3}
          size="small"
          slotProps={{ htmlInput: { maxLength: lengthRestrictions.product.title } }}
          sx={styles.titleInput}
        />
        <Typography
          sx={
            [
              styles.charCount,
              { color: isTitleOverLimit ? 'error.main' : 'text.secondary' },
            ] as SxProps<Theme>
          }
        >
          {currentTitle.length}/{lengthRestrictions.product.title}
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={styles.actionsStack}>
        {isAmazon && (
          <Tooltip
            title={
              hasHardcodedLink
                ? (getHardcodedLinkSourceLabel(productHardcodedLinkSource) ?? 'Attribution link')
                : ''
            }
          >
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              <Button
                color="primary"
                disableRipple
                fullWidth
                onClick={() => setIsGenerateLinkModalOpen(true)}
                size="small"
                startIcon={(
                  <Box sx={styles.attributionSourceIcon}>
                    <AttributionLinkButtonIcon
                      isHardcoded={hasHardcodedLink}
                      source={productHardcodedLinkSource}
                    />
                  </Box>
                )}
                sx={styles.actionBtnPartner}
                variant="outlined"
              >
                Attribution Link
              </Button>
            </Box>
          </Tooltip>
        )}
        <Button
          color="error"
          disableRipple
          onClick={handleDeleteClick}
          size="small"
          startIcon={<DeleteOutlineIcon />}
          sx={styles.actionBtnReplace}
          variant="text"
        >
          Delete Product
        </Button>
      </Stack>
    </Box>
  );
};
